import { clamp } from '../core/math';
/* ============================================================
   高度場 —— 「這個位置的地面有多高」與「怎麼爬上去」
   ============================================================

   規格（使用者逐題拍板）：`.claude/notes/flight-spec.md` 的地圖那六題。
   其中兩條直接決定了這個檔的形狀：

   - **決定 5「做可站立平台」** → 地面高度不再是 0，要有一個高度場。
   - **決定 6「每座高台都有斜坡，地面敵人照樣追上來」** → 高度場不只要
     回答「多高」，還要回答「從這裡怎麼上去」（`climbWaypoint`）。
     沒有後者的話高台就是安全屋，那條決定等於沒做。

   三個「只有這樣做才成立」的地方：

   1. **這個檔不 import `world/arena`**（反向也不會有循環）。可站立的構件
      由 arena 在重建地形後呼叫 `reindex(obstacles)` 餵進來。純資料層 ——
      沒有 THREE、沒有場景，所以單元測試可以直接餵假構件驗高度。
   2. **爬升能力是呼叫端給的參數（`climb`），不是這裡的常數。**
      走路 `STEP`、衝刺 `VAULT` —— 「衝刺能過矮掩體、跑步會被擋」
      （決定 2）就是同一個判定吃不同的 climb，不是兩套程式碼。
   3. **斜坡不是障礙物**，所以它不進 `obstacles`，只進這裡的 `ramps`。
      進了 `obstacles` 就會被 `resolveCircle` 推開，變成一道爬不上去的牆。 */

/** 走得上去的高度差。**1 單位 = 1 公尺**，機體 4.4 m ——
    1.0 m 約是它的膝蓋：低於就直接踏上去（樓梯感），高於就是一堵牆。 */
export var STEP = 1.0;
/** 衝刺翻得過去的高度。**這條數字就是「矮掩體」的定義**。

    **1 單位 = 1 公尺**（使用者 2026-08-03 拍板，跟著 `09-level-design.md`）。
    2.8 m ≈ 機體（4.4 m）的胸口，而且剛好讓「單層貨櫃（2.6 m）翻得過、
    雙層（5.2 m）翻不過」—— 那一組對比就是玩家學會這條規則的教材。 */
export var VAULT = 2.8;

/** 站得上去的最高高度（`09-level-design.md` §182「可攀爬高台 ≤8 m」）。
    比這個高的樓是**純障礙**：不可站、不給坡 —— 高度層級因此收在
    「地面／矮掩體／屋頂」三層，文件 §169 要的就是這件事。 */
export var CLIMB_MAX = 8;

/** 斜坡。沿**本地 +z** 從 `y0` 升到 `y1`（所以 `rot` 決定坡面朝哪）。 */
export var ramps = [];

var CELL = 9;
var idx = new Map<number, any[]>();

/* ⚠️ **鍵一定要是整數，不能是 `i + ',' + j`。**
   `groundYAt` 是每隻敵人每一步都會問一次的（300 隻 × 60 步 = 每秒 1.8 萬次），
   字串鍵等於每秒配置好幾萬個短字串 —— 實測整個測試套件從 83 秒變成 242 秒。
   `(i+2048)*4096 + (j+2048)` 在 ±2048 格（±18000 單位）內不會碰撞。 */
function key(x, z) {
  return (Math.floor(x / CELL) + 2048) * 4096 + Math.floor(z / CELL) + 2048;
}
function put(o, rad) {
  var x0 = Math.floor((o.x - rad) / CELL), x1 = Math.floor((o.x + rad) / CELL);
  var z0 = Math.floor((o.z - rad) / CELL), z1 = Math.floor((o.z + rad) / CELL);
  for (var i = x0; i <= x1; i++) for (var j = z0; j <= z1; j++) {
    var k = (i + 2048) * 4096 + j + 2048;
    var a = idx.get(k); if (!a) idx.set(k, a = []);
    a.push(o);
  }
}
function radOf(o) {
  return o.kind === 1 ? o.r : Math.hypot(o.hw, o.hd);
}

export function clearRamps() {
  ramps.length = 0;
}
/** 加一條斜坡。`hd` 是**沿坡長度的一半**，`hw` 是寬度的一半。 */
export function addRamp(r) {
  ramps.push(r);
  return r;
}

/** 重建空間索引。**地形一改就要呼叫**（重建、被 Boss 轟掉、碾掉都算）——
    索引沒更新的話玩家會站在一塊已經不存在的平台上。 */
export function reindex(obstacles) {
  idx = new Map();
  for (var i = 0; i < obstacles.length; i++) {
    var o = obstacles[i];
    if (o.stand) put(o, radOf(o) + 0.5);
  }
  for (var j = 0; j < ramps.length; j++) put(ramps[j], radOf(ramps[j]) + 0.5);
}

/** 本地座標。回傳沿 z 的分量，沿 x 的放在 `_lx`（一次算完兩個，
    高度查詢在 300 隻同屏時是熱路徑，不要為了漂亮多配一個物件）。 */
var _lx = 0;
function localZ(o, x, z) {
  var c = Math.cos(o.rot || 0), s = Math.sin(o.rot || 0);
  var dx = x - o.x, dz = z - o.z;
  _lx = dx * c + dz * s;
  return -dx * s + dz * c;
}

/** 某個構件在 (x,z) 的頂面高度；不在它上方就回 `MISS`。

    ⚠️ `MISS` 一定要比任何可能的地面都低。曾經是 -1，接了移植進來的世界
    （裂谷 -30、崩塌坑 -36）之後就爛掉了：站在谷底時「沒踩到任何構件」
    會回 -1，比谷底 -30 還高 —— 玩家與敵人整片浮在谷上方 29 公尺。 */
var MISS = -1e9;
function topAt(o, x, z) {
  if (o.y0 !== undefined) {                       // 斜坡
    var lz = localZ(o, x, z);
    if (Math.abs(_lx) > o.hw || Math.abs(lz) > o.hd) return MISS;
    return o.y0 + (o.y1 - o.y0) * clamp((lz + o.hd) / (2 * o.hd), 0, 1);
  }
  if (o.kind === 1) return Math.hypot(x - o.x, z - o.z) < o.r ? o.h : MISS;
  var lz2 = localZ(o, x, z);
  return (Math.abs(_lx) < o.hw && Math.abs(lz2) < o.hd) ? o.h : MISS;
}

/* 地表基準 ----------------------------------------------------
   程序生成的街廓是平的（基準 0）；移植進來的世界有真正的地形
   （`world/proto-height.ts`），所以基準改成可抽換的一支函式。
   **只改這裡一處**，飛行的落地、敵人的貼地、投射物的地面就全部跟上。 */
var groundBase: ((x: number, z: number) => number) | null = null;
export function setGroundBase(fn) { groundBase = fn; }
export function clearGroundBase() { groundBase = null; }

/** 某個位置的**地面高度**（沒有地形也沒有構件就是 0）。

    ⚠️ 任何「掉到地上了沒」的判斷都要問它，**不要寫死 0**。
    飛行的落地判定（`sim/flight.ts`）、敵人的貼地高度、投射物的地面
    都是同一個來源 —— 三邊各寫一份的話，玩家會站在敵人走不到的高度上。 */
export function groundYAt(x, z) {
  var y = groundBase ? groundBase(x, z) : 0;
  var c = idx.get(key(x, z));
  if (!c) return y;
  for (var i = 0; i < c.length; i++) {
    var t = topAt(c[i], x, z);
    if (t > y) y = t;
  }
  return y;
}

/** **只有地表**的高度（不含構件頂面）。給「地面層有多少可走區」這類
    版型量測用（`world/layout-metrics.ts`）—— 用 `groundYAt` 的話，
    站在六層樓屋頂上會被算成「可走」，於是一座城市量出來是三百塊碎片。 */
export function groundBaseAt(x, z) {
  return groundBase ? groundBase(x, z) : 0;
}

/** 這塊構件擋不擋得住「站在 `fromY`、爬升能力 `climb` 的東西」。
    `fromY` 省略＝什麼都擋（舊行為，給不吃高度的呼叫端用）。 */
export function blocksAt(o, fromY, climb) {
  if (fromY === undefined) return true;
  return o.h > fromY + (climb === undefined ? STEP : climb);
}

/* 斜坡尋路 ----------------------------------------------------

   決定 6：**高處只給視野與射界，不給安全**。所以地面敵人看到玩家在
   構不到的高度時，不會傻站在崖壁下，而是去找一條上得來的坡。

   刻意做得很小：兩個路點（坡底 → 坡頂），不是尋路系統。理由是
   敵人本來就沒有 navmesh（現況是「直線衝＋被擋就沿切線滑」），
   為了平台硬塞一套 A* 會讓 300 隻同屏的成本失控 ——
   而這張地圖的坡數量是個位數，最近的那一條幾乎永遠就是對的。 */

/** 從 (x,z,myY) 要上到 `wantY`，下一個該走的點。已經夠高就回 null。 */
export function climbWaypoint(x, z, myY, wantY) {
  if (wantY - myY <= STEP) return null;
  var best = null, bd = 1e9, bOn = false;
  for (var i = 0; i < ramps.length; i++) {
    var r = ramps[i];
    var hi = r.y1 > r.y0 ? r.y1 : r.y0, lo = r.y1 > r.y0 ? r.y0 : r.y1;
    if (hi < wantY - STEP) continue;              // 這條坡到不了那個高度
    if (lo > myY + STEP) continue;                // 坡底我就上不去
    var c = Math.cos(r.rot || 0), s = Math.sin(r.rot || 0);
    var dir = r.y1 > r.y0 ? 1 : -1;               // 本地 +z 是上坡還是下坡
    var bz = -dir * r.hd, tz = dir * r.hd;
    var bx = r.x - s * bz, bzw = r.z + c * bz;    // 坡底世界座標
    var lz = localZ(r, x, z);
    var on = Math.abs(_lx) < r.hw + 1.2 && Math.abs(lz) < r.hd + 1.2;
    var d = on ? 0 : (x - bx) * (x - bx) + (z - bzw) * (z - bzw);
    if (d < bd) {
      bd = d; bOn = on;
      best = on
        ? { x: r.x - s * tz, z: r.z + c * tz }    // 已經在坡上 → 直接往坡頂
        : { x: bx, z: bzw };
    }
  }
  _onRamp = bOn;
  return best;
}
var _onRamp = false;
/** 上一次 `climbWaypoint` 選中的坡，呼叫者是不是已經站在它上面。 */
export function onRamp() {
  return _onRamp;
}

/** 除錯讀數。 */
export function terrainStats() {
  return { ramps: ramps.length, cells: idx.size };
}
